import React, { createContext, useState, useCallback, ReactNode } from "react";
import api from "../../api/api";
import { PaginationInfo } from "../../types/api";

interface TeamMember {
  name: string;
  contact: string;
  work_type: string;
  daily_wage: number;
}

interface Labour {
  id?: number;
  type: "Individual" | "Group";
  name: string;
  contact: string;
  alternate_contact?: string;
  work_type?: string;
  wage_type?: string;
  fixed_wage?: number | string;
  daily_wage?: number | string;
  overtime_rate?: number | string;
  ta_per_day?: number | string;
  food_allowance?: number | string;
  opening_balance?: number | string;
  aadhar_no?: string;
  address?: string;
  photo?: string | File;
  documents?: FileList | File[] | null;
  status?: string;
  team_members?: TeamMember[];
}

interface LabourContextType {
  labours: Labour[];
  pagination: PaginationInfo | null;
  loading: boolean;
  fetchLabours: (filters?: { type?: string; status?: string; work_type?: string }, page?: number, perPage?: number) => Promise<void>;
  addLabour: (labour: Labour) => Promise<void>;
  editLabour: (id: number, labour: Labour) => Promise<void>;
  deleteLabour: (id: number) => Promise<void>;
  getLabourById: (id: number) => Labour | undefined;
}

export const LabourContext = createContext<LabourContextType | undefined>(undefined);

interface LabourProviderProps {
  children: ReactNode;
}

export const LabourProvider: React.FC<LabourProviderProps> = ({ children }) => {
  const [labours, setLabours] = useState<Labour[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const fetchLabours = useCallback(async (
    filters?: { type?: string; status?: string; work_type?: string },
    page: number = 1,
    perPage: number = 15
  ) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        per_page: perPage.toString(),
      });
      if (filters?.type) params.append("type", filters.type);
      if (filters?.status) params.append("status", filters.status);
      if (filters?.work_type) params.append("work_type", filters.work_type);

      const url = `/labours?${params.toString()}`;
      const response = await api.get(url);
      
      // Handle paginated response structure: { success: true, data: { data: [...], pagination: {...} } }
      let labourData: Labour[] = [];
      let paginationData: PaginationInfo | null = null;
      const responseData = response.data;
      
      if (responseData?.success) {
        if (responseData.data?.data && Array.isArray(responseData.data.data)) {
          // Standard pagination structure: data.data contains array, data.pagination contains pagination info
          labourData = responseData.data.data;
          paginationData = responseData.data.pagination || responseData.pagination;
        } else if (responseData.data && Array.isArray(responseData.data)) {
          // Direct array structure
          labourData = responseData.data;
          paginationData = responseData.pagination;
        } else if (Array.isArray(responseData)) {
          labourData = responseData;
        }
      } else if (Array.isArray(responseData?.data)) {
        labourData = responseData.data;
        paginationData = responseData.pagination;
      } else if (Array.isArray(responseData)) {
        labourData = responseData;
      }
      
      setLabours(labourData);
      setPagination(paginationData);
    } catch (error) {
      console.error("Failed to fetch labours:", error);
      setLabours([]);
      setPagination(null);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  const addLabour = async (labour: Labour) => {
    try {
      const formData = new FormData();

      // Append basic fields
      formData.append("type", labour.type || "Individual");
      formData.append("name", labour.name);
      formData.append("contact", labour.contact);
      if (labour.alternate_contact) formData.append("alternate_contact", labour.alternate_contact);
      if (labour.work_type) formData.append("work_type", labour.work_type);
      if (labour.wage_type) formData.append("wage_type", labour.wage_type);
      if (labour.fixed_wage !== undefined && labour.fixed_wage !== null && labour.fixed_wage !== "") {
        formData.append("fixed_wage", labour.fixed_wage.toString());
      }
      if (labour.daily_wage !== undefined && labour.daily_wage !== null && labour.daily_wage !== "") {
        formData.append("daily_wage", labour.daily_wage.toString());
      }
      if (labour.overtime_rate !== undefined && labour.overtime_rate !== null && labour.overtime_rate !== "") {
        formData.append("overtime_rate", labour.overtime_rate.toString());
      }
      if (labour.ta_per_day !== undefined && labour.ta_per_day !== null && labour.ta_per_day !== "") {
        formData.append("ta_per_day", labour.ta_per_day.toString());
      }
      if (labour.food_allowance !== undefined && labour.food_allowance !== null && labour.food_allowance !== "") {
        formData.append("food_allowance", labour.food_allowance.toString());
      }
      if (labour.opening_balance !== undefined && labour.opening_balance !== null && labour.opening_balance !== "") {
        formData.append("opening_balance", labour.opening_balance.toString());
      }
      if (labour.aadhar_no) formData.append("aadhar_no", labour.aadhar_no);
      if (labour.address) formData.append("address", labour.address);
      if (labour.status) formData.append("status", labour.status);

      // Handle photo file
      if (labour.photo instanceof File) {
        formData.append("photo", labour.photo);
      }

      // Handle documents (if FileList)
      if (labour.documents instanceof FileList) {
        Array.from(labour.documents).forEach((file) => {
          formData.append("documents[]", file);
        });
      }

      // Handle team members for Group type
      if (labour.type === "Group" && labour.team_members && Array.isArray(labour.team_members)) {
        labour.team_members.forEach((member, index) => {
          formData.append(`team_members[${index}][name]`, member.name);
          formData.append(`team_members[${index}][contact]`, member.contact);
          formData.append(`team_members[${index}][work_type]`, member.work_type);
          formData.append(`team_members[${index}][daily_wage]`, member.daily_wage.toString());
        });
      }

      const response = await api.post("/labours", formData);

      const newLabour = response.data.data || response.data;
      setLabours((prev) => [...prev, newLabour]);
    } catch (error) {
      console.error("Failed to add labour:", error);
      throw error;
    }
  };

  const editLabour = async (id: number, labour: Labour) => {
    try {
      const formData = new FormData();

      // Append basic fields (same as addLabour)
      formData.append("type", labour.type || "Individual");
      formData.append("name", labour.name);
      formData.append("contact", labour.contact);
      if (labour.alternate_contact) formData.append("alternate_contact", labour.alternate_contact);
      if (labour.work_type) formData.append("work_type", labour.work_type);
      if (labour.wage_type) formData.append("wage_type", labour.wage_type);
      if (labour.fixed_wage !== undefined && labour.fixed_wage !== null && labour.fixed_wage !== "") {
        formData.append("fixed_wage", labour.fixed_wage.toString());
      }
      if (labour.daily_wage !== undefined && labour.daily_wage !== null && labour.daily_wage !== "") {
        formData.append("daily_wage", labour.daily_wage.toString());
      }
      if (labour.overtime_rate !== undefined && labour.overtime_rate !== null && labour.overtime_rate !== "") {
        formData.append("overtime_rate", labour.overtime_rate.toString());
      }
      if (labour.ta_per_day !== undefined && labour.ta_per_day !== null && labour.ta_per_day !== "") {
        formData.append("ta_per_day", labour.ta_per_day.toString());
      }
      if (labour.food_allowance !== undefined && labour.food_allowance !== null && labour.food_allowance !== "") {
        formData.append("food_allowance", labour.food_allowance.toString());
      }
      if (labour.opening_balance !== undefined && labour.opening_balance !== null && labour.opening_balance !== "") {
        formData.append("opening_balance", labour.opening_balance.toString());
      }
      if (labour.aadhar_no) formData.append("aadhar_no", labour.aadhar_no);
      if (labour.address) formData.append("address", labour.address);
      if (labour.status) formData.append("status", labour.status);

      // Handle photo file (only if new file is uploaded)
      if (labour.photo instanceof File) {
        formData.append("photo", labour.photo);
      }

      // Handle documents (if FileList)
      if (labour.documents instanceof FileList) {
        Array.from(labour.documents).forEach((file) => {
          formData.append("documents[]", file);
        });
      }

      // Handle team members for Group type
      if (labour.type === "Group" && labour.team_members && Array.isArray(labour.team_members)) {
        labour.team_members.forEach((member, index) => {
          formData.append(`team_members[${index}][name]`, member.name);
          formData.append(`team_members[${index}][contact]`, member.contact);
          formData.append(`team_members[${index}][work_type]`, member.work_type);
          formData.append(`team_members[${index}][daily_wage]`, member.daily_wage.toString());
        });
      }

      formData.append("_method", "PUT");
      const response = await api.post(`/labours/${id}`, formData);

      const updatedLabour = response.data.data || response.data;
      setLabours((prev) =>
        prev.map((lab) => (lab.id === id ? updatedLabour : lab))
      );
    } catch (error) {
      console.error("Failed to edit labour:", error);
      throw error;
    }
  };

  const deleteLabour = async (id: number) => {
    try {
      await api.delete(`/labours/${id}`);
      setLabours((prev) => prev.filter((lab) => lab.id !== id));
    } catch (error) {
      console.error("Failed to delete labour:", error);
      throw error;
    }
  };

  const getLabourById = (id: number): Labour | undefined => {
    return labours.find((lab) => lab.id === id);
  };

  // Remove auto-fetch on mount to prevent infinite loops
  // Data will be fetched when explicitly called from components with pagination

  return (
    <LabourContext.Provider
      value={{
        labours,
        pagination,
        loading,
        fetchLabours,
        addLabour,
        editLabour,
        deleteLabour,
        getLabourById,
      }}
    >
      {children}
    </LabourContext.Provider>
  );
};

export default LabourProvider;
